<template>
  <ListViewBanner :add-route="{ name: 'app_user_new' }">
    Users
  </ListViewBanner>

  <UserSearch @search="searchParams = $event" />

  <AppContainer>
    <AppTable>
      <AppTableHeader>
        <th>Name</th>
        <th>Role</th>
        <th>Districts</th>
        <th>Last Active</th>
        <th><span class="sr-only">Actions</span></th>
      </AppTableHeader>

      <AppTableBody
        :loading="loading"
        :no-results="users.length === 0"
        :colspan="5"
      >
        <template v-if="users.length">
          <tr
            v-for="user in users"
            :key="user.id"
            class="overflow-visible"
          >
            <td>
              <span>{{ user.firstName }} {{ user.lastName }}</span>

              <template v-if="user.archived">
                <BadgeArchived />
              </template>
            </td>

            <td>
              <span
                v-for="(userRole, index) in user.roles"
                :key="index"
              >
                {{ userRole }}<template v-if="index !== user.roles.length - 1">,&nbsp;</template>
              </span>
            </td>

            <td>
              <div class="flex flex-col">
                <span>
                  {{ user.districts[0]?.name }}
                </span>

                <template v-if="user.districts.length > 1">
                  <UsersTooltip
                    :user="user"
                    :districts="user.districts"
                    class="users-list-tooltip"
                  />
                </template>
              </div>
            </td>

            <td>
              {{ dateFormatter.formatShortDate(user.lastLoginAt) }}
            </td>

            <td>
              <template v-if="user.archived">
                <RestoreButton @click="entityToRestore = user" />
              </template>

              <template v-else>
                <AppDropdown>
                  <AppDropdownItem
                    text="Edit"
                    :to="{ name: 'app_user_edit', params: { user: user.id } }"
                  />

                  <AppDropdownItem
                    text="Archive"
                    @click="entityToArchive = user"
                  />

                  <AppDropdownItem
                    v-if="!user.activated"
                    text="Resend activation email"
                    @click="resendActivationEmail(user)"
                  />
                </AppDropdown>
              </template>
            </td>
          </tr>
        </template>
      </AppTableBody>
    </AppTable>

    <AppPagination
      v-model:itemsPerPage="paginationData.itemsPerPage"
      v-model:currentPage="paginationData.page"
      :items-per-page="paginationData?.itemsPerPage"
      :current-page="paginationData?.page"
      :total-items="paginationData?.total"
      @page-change="searchParams.page = $event"
      @items-per-page-change="searchParams.itemsPerPage = $event"
    />
  </AppContainer>

  <!-- Archive Modal -->
  <template v-if="entityToArchive">
    <ConfirmationModal
      title="Archive User"
      :processing="archiving"
      @confirm="archiveEntity"
      @close="entityToArchive = false"
    >
      <p>
        <span class="block">Name: {{ entityToArchive?.firstName }} {{ entityToArchive?.lastName }}</span>
        Are you sure you want to <strong>ARCHIVE</strong> this User?
      </p>
    </ConfirmationModal>
  </template>

  <!-- Restore Modal -->
  <template v-if="entityToRestore">
    <ConfirmationModal
      title="Restore User"
      :processing="restoring"
      @confirm="restoreEntity"
      @close="entityToRestore = false"
    >
      <p>
        <span class="block">Name: {{ entityToRestore?.firstName }} {{ entityToRestore?.lastName }}</span>
        Are you sure you want to <strong>RESTORE</strong> this User?
      </p>
    </ConfirmationModal>
  </template>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { ref, watchEffect } from 'vue';

import { useDateFormatter, useEntityArchiver, useEntityRestorer, useHttp, useToasts } from '@/composables';

import { AppContainer, AppPagination } from '@/components/app';
import { BadgeArchived } from '@/components/badges';
import { ListViewBanner } from '@/components/banners';
import { RestoreButton } from '@/components/buttons';
import { AppDropdown, AppDropdownItem } from '@/components/dropdowns';
import { ConfirmationModal } from '@/components/modals';
import { UserSearch } from '@/components/forms/search';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables';
import { UsersTooltip } from '@/components/tooltips';

const toasts = useToasts();
const dateFormatter = useDateFormatter();

const loading = ref();
const users = ref([]);
const searchParams = ref({
  page: 1,
});
const paginationData = ref({
  page: 1,
  itemsPerPage: 10,
});

const loadUsers = async () => {
  loading.value = true;
  users.value = [];

  const response = await useHttp().get('/users', searchParams.value);

  if (response.statusCode === HttpStatusCode.Ok) {
    users.value = response.data.items;
    paginationData.value = response.data.pagination;
  }

  loading.value = false;
};

const resendActivationEmail = async user => {
  const response = await useHttp().post(`/users/${user.id}/activation`);

  if (response.statusCode === HttpStatusCode.Ok) {
    toasts.success(`An activation email for ${user.firstName} ${user.lastName} has been sent`);
  }
};

const { archiving, entityToArchive, archiveEntity } = useEntityArchiver('users', loadUsers);
const { restoring, entityToRestore, restoreEntity } = useEntityRestorer('users', loadUsers);

watchEffect(loadUsers);
</script>

